import { useState, useEffect } from 'react';
import { gruposVacaciones } from '../services/api';
import { useToast } from '../context/ToastContext';
import { ChevronRightIcon, CalendarDaysIcon, ArrowPathIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Modal from '../components/Modal';

// Available years for selection
const AVAILABLE_YEARS = [2024, 2025, 2026, 2027];

export default function GruposVacaciones() {
    const [grupos, setGrupos] = useState([]);
    const [selectedGrupo, setSelectedGrupo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedYear, setSelectedYear] = useState(2026);
    const [tipoFilter, setTipoFilter] = useState('bei');

    // Modals state
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [newGroupData, setNewGroupData] = useState({ numero: '', nombre: '' });
    const [groupToDelete, setGroupToDelete] = useState(null);

    // Configuration state for the selected group
    const [config, setConfig] = useState({
        mes: { inicio: '', fin: '' },
        quincena1: { inicio: '', fin: '' },
        quincena2: { inicio: '', fin: '' },
        quincena3: { inicio: '', fin: '' }
    });

    const { success, error: toastError } = useToast();

    useEffect(() => {
        fetchGrupos();
    }, [selectedYear, tipoFilter]);

    useEffect(() => {
        if (selectedGrupo) {
            // Parse existing config or use defaults
            const savedConfig = selectedGrupo.configuracion_periodos;
            if (savedConfig) {
                // Ensure all fields exist
                setConfig({
                    mes: savedConfig.mes || { inicio: '', fin: '' },
                    quincena1: savedConfig.quincena1 || { inicio: '', fin: '' },
                    quincena2: savedConfig.quincena2 || { inicio: '', fin: '' },
                    quincena3: savedConfig.quincena3 || { inicio: '', fin: '' }
                });
            } else {
                setConfig({
                    mes: { inicio: '', fin: '' },
                    quincena1: { inicio: '', fin: '' },
                    quincena2: { inicio: '', fin: '' },
                    quincena3: { inicio: '', fin: '' }
                });
            }
        }
    }, [selectedGrupo]);

    const fetchGrupos = async () => {
        try {
            setLoading(true);
            setSelectedGrupo(null);
            const response = await gruposVacaciones.getAll(selectedYear, tipoFilter);
            setGrupos(response.grupos || []);
        } catch (error) {
            console.error(error);
            toastError('Error al cargar grupos');
        } finally {
            setLoading(false);
        }
    };

    // --- Create Modal Handlers ---
    const openCreateModal = () => {
        setNewGroupData({ numero: '', nombre: '' });
        setShowCreateModal(true);
    };

    const handleCreateConfirm = async () => {
        if (!newGroupData.numero) {
            toastError("El número es obligatorio");
            return;
        }

        try {
            const numeroInt = parseInt(newGroupData.numero);
            const nombreFinal = newGroupData.nombre || `Grupo ${numeroInt}`;

            await gruposVacaciones.create({
                numero: numeroInt,
                nombre: nombreFinal,
                periodo_vacaciones: '2026',
                tipo: tipoFilter
            });
            success('Grupo vacacional creado');
            fetchGrupos();
            setShowCreateModal(false);
        } catch (error) {
            toastError('Error al crear: ' + error.message);
        }
    };

    // --- Delete Modal Handlers ---
    const openDeleteModal = (e, grupo) => {
        e.stopPropagation();
        setGroupToDelete(grupo);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!groupToDelete) return;

        try {
            await gruposVacaciones.delete(groupToDelete.id);
            success('Grupo eliminado');
            setGrupos(prev => prev.filter(g => g.id !== groupToDelete.id));
            if (selectedGrupo?.id === groupToDelete.id) {
                setSelectedGrupo(null);
            }
            setShowDeleteModal(false);
            setGroupToDelete(null);
        } catch (error) {
            toastError('Error al eliminar: ' + error.message);
        }
    };

    const handleSave = async () => {
        if (!selectedGrupo) return;
        setSaving(true);
        try {
            // Construct payload
            const payload = {
                configuracion_periodos: config
            };

            await gruposVacaciones.update(selectedGrupo.id, payload);
            success('Configuración guardada correctamente');

            // Refresh local data
            const updatedGrupos = grupos.map(g =>
                g.id === selectedGrupo.id ? { ...g, configuracion_periodos: config } : g
            );
            setGrupos(updatedGrupos);
            // Update selectedGrupo reference to keep sync
            setSelectedGrupo({ ...selectedGrupo, configuracion_periodos: config });

        } catch (error) {
            console.error(error);
            toastError('Error al guardar configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleDateChange = (period, field, value) => {
        setConfig(prev => ({
            ...prev,
            [period]: {
                ...prev[period],
                [field]: value
            }
        }));
    };

    // Helper to calculate left % and width % for timeline
    const getTimelineStyle = (startStr, endStr) => {
        if (!startStr || !endStr) return null;
        const start = new Date(startStr);
        const end = new Date(endStr);
        const startYear = new Date('2026-01-01');
        const endYear = new Date('2026-12-31');
        const totalMs = endYear - startYear;

        let leftMs = start - startYear;
        // Clamp
        if (leftMs < 0) leftMs = 0;

        let widthMs = end - start;
        if (widthMs < 0) widthMs = 0;

        const left = (leftMs / totalMs) * 100;
        const width = (widthMs / totalMs) * 100;

        return { left: `${left}%`, width: `${width}%` };
    };

    const renderTimelineBlock = (periodKey, colorClass, label) => {
        const { inicio, fin } = config[periodKey];
        const style = getTimelineStyle(inicio, fin);

        if (!style) return null;

        return (
            <div
                className={`absolute h-full rounded-md shadow-sm border border-black/20 ${colorClass} transition-all duration-300 group`}
                style={style}
                title={`${label}: ${inicio} - ${fin}`}
            >
                <div className="hidden group-hover:block absolute bottom-full mb-1 left-0 bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                    {label}
                </div>
            </div>
        );
    };

    if (loading) return <div className="p-8 text-white">Cargando grupos...</div>;

    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

    return (
        <div className="flex h-screen overflow-hidden">
            {/* Sidebar List */}
            <div className="w-80 bg-[#0f0f23] border-r border-white/10 overflow-y-auto flex flex-col shrink-0">
                <div className="p-6 border-b border-white/10 flex justify-between items-start">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            <CalendarDaysIcon className="w-6 h-6 text-cyan-400" />
                            Grupos
                        </h2>
                        <p className="text-gray-400 text-xs mt-1">Configuración Vacaciones</p>
                    </div>
                    <button
                        onClick={openCreateModal}
                        className="p-1.5 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500 hover:text-white transition-colors"
                        title="Nuevo Grupo"
                    >
                        <PlusIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Tipo Filter */}
                <div className="px-4 pt-4">
                    <div className="flex bg-[#0f0f23] rounded-lg p-1 border border-white/10">
                        <button
                            onClick={() => setTipoFilter('bei')}
                            className={`flex-1 text-xs py-2 rounded-md transition-all font-semibold ${tipoFilter === 'bei' ? 'bg-cyan-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                        >
                            🚌 BEI
                        </button>
                        <button
                            onClick={() => setTipoFilter('servicio')}
                            className={`flex-1 text-xs py-2 rounded-md transition-all font-semibold ${tipoFilter === 'servicio' ? 'bg-blue-500 text-white shadow' : 'text-gray-400 hover:text-white'}`}
                        >
                            🚍 Servicio
                        </button>
                    </div>
                </div>

                {/* Year Selector */}
                <div className="px-4 pt-4 pb-2">
                    <label className="block text-xs text-gray-500 mb-1">Año</label>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="w-full bg-[#1a1a3e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500"
                    >
                        {AVAILABLE_YEARS.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
                <div className="space-y-2 p-4">
                    {grupos.map(group => (
                        <div key={group.id} className="relative group/item">
                            <button
                                onClick={() => setSelectedGrupo(group)}
                                className={`w-full text-left p-4 pr-10 rounded-xl transition-all border ${selectedGrupo?.id === group.id
                                    ? 'bg-cyan-500/20 border-cyan-500/50 text-white shadow-lg shadow-cyan-500/10'
                                    : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10 hover:text-white'
                                    }`}
                            >
                                <div className="font-semibold text-sm">{group.nombre}</div>
                                <div className="text-xs mt-1 opacity-70 truncate">
                                    {group.conductores?.split(',').slice(0, 3).join(', ')}
                                    {group.conductores?.split(',').length > 3 && '...'}
                                    {!group.conductores && 'Sin conductores'}
                                </div>
                            </button>
                            <button
                                onClick={(e) => openDeleteModal(e, group)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-red-400 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                title="Eliminar grupo"
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                    {grupos.length === 0 && (
                        <div className="text-center text-gray-500 text-sm py-8">
                            No hay grupos creados
                        </div>
                    )}
                </div>
            </div>

            {/* Main Editor */}
            <div className="flex-1 bg-[#1a1a3e] p-8 overflow-y-auto">
                {selectedGrupo ? (
                    <div className="max-w-4xl mx-auto space-y-8 pb-10">
                        <div className="flex justify-between items-center">
                            <div>
                                <h1 className="text-3xl font-bold text-white mb-2">Editor de Vacaciones</h1>
                                <p className="text-gray-400">Configurando: <span className="text-cyan-400 font-semibold">{selectedGrupo.nombre}</span></p>
                            </div>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl font-bold hover:scale-105 transition-all shadow-lg shadow-emerald-500/20 flex items-center gap-2 disabled:opacity-50"
                            >
                                {saving ? <ArrowPathIcon className="w-5 h-5 animate-spin" /> : 'Guardar Cambios'}
                            </button>
                        </div>

                        {/* VISUAL TIMELINE */}
                        <div className="bg-white/5 p-6 rounded-2xl border border-white/10 shadow-xl">
                            <h3 className="text-white font-semibold mb-4">Línea de Tiempo 2026</h3>
                            <div className="relative h-24 bg-black/20 rounded-lg border border-white/5 mb-8">
                                {/* Grid Lines for Months */}
                                <div className="absolute inset-0 flex">
                                    {months.map(m => (
                                        <div key={m} className="flex-1 border-r border-white/5 relative group">
                                            <span className="absolute -bottom-6 left-2 text-xs text-gray-500">{m}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Render Blocks */}
                                <div className="absolute inset-y-2 inset-x-0">
                                    {renderTimelineBlock('mes', 'bg-blue-500/80', 'Mes Completo')}
                                    {renderTimelineBlock('quincena1', 'bg-emerald-500/80', '1ª Quincena')}
                                    {renderTimelineBlock('quincena2', 'bg-emerald-500/80', '2ª Quincena')}
                                    {renderTimelineBlock('quincena3', 'bg-emerald-500/80', '3ª Quincena')}
                                </div>
                            </div>
                        </div>

                        {/* INPUTS GRID */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Mes Completo */}
                            <div className="bg-blue-500/10 p-6 rounded-2xl border border-blue-500/20">
                                <h3 className="text-blue-400 font-bold mb-4 text-lg">Vacaciones de Mes</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs uppercase tracking-wider text-blue-300 mb-1">Inicio</label>
                                        <input
                                            type="date"
                                            className="w-full bg-[#0f0f23] border border-blue-500/30 rounded-lg p-2 text-white focus:outline-none focus:border-blue-500"
                                            value={config.mes.inicio}
                                            onChange={(e) => handleDateChange('mes', 'inicio', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs uppercase tracking-wider text-blue-300 mb-1">Fin</label>
                                        <input
                                            type="date"
                                            className="w-full bg-[#0f0f23] border border-blue-500/30 rounded-lg p-2 text-white focus:outline-none focus:border-blue-500"
                                            value={config.mes.fin}
                                            onChange={(e) => handleDateChange('mes', 'fin', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Quincenas Container */}
                            <div className="space-y-4">
                                {[1, 2, 3].map(num => (
                                    <div key={num} className="bg-emerald-500/10 p-4 rounded-xl border border-emerald-500/20 flex items-center gap-4">
                                        <div className="w-24 font-bold text-emerald-400">Quincena {num}</div>
                                        <div className="flex-1 grid grid-cols-2 gap-2">
                                            <input
                                                type="date"
                                                className="bg-[#0f0f23] border border-emerald-500/30 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                                                value={config[`quincena${num}`].inicio}
                                                onChange={(e) => handleDateChange(`quincena${num}`, 'inicio', e.target.value)}
                                            />
                                            <input
                                                type="date"
                                                className="bg-[#0f0f23] border border-emerald-500/30 rounded-lg p-2 text-white text-sm focus:outline-none focus:border-emerald-500"
                                                value={config[`quincena${num}`].fin}
                                                onChange={(e) => handleDateChange(`quincena${num}`, 'fin', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                ) : (
                    <div className="h-full flex flex-col items-center justify-center text-gray-500 opacity-50">
                        <CalendarDaysIcon className="w-24 h-24 mb-4" />
                        <p className="text-xl">Selecciona un grupo para editar sus vacaciones</p>
                    </div>
                )}
            </div>

            {/* Modals */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Crear Nuevo Grupo"
                footer={
                    <>
                        <button
                            onClick={() => setShowCreateModal(false)}
                            className="text-gray-400 hover:text-white px-4 py-2"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleCreateConfirm}
                            className="bg-cyan-500 hover:bg-cyan-400 text-white px-4 py-2 rounded-lg font-bold"
                        >
                            Crear Grupo
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Número de Grupo</label>
                        <input
                            type="number"
                            className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-white focus:border-cyan-500 focus:outline-none"
                            placeholder="Ej: 10"
                            value={newGroupData.numero}
                            onChange={e => setNewGroupData({ ...newGroupData, numero: e.target.value })}
                            autoFocus
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-400 mb-1">Nombre (Opcional)</label>
                        <input
                            type="text"
                            className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-white focus:border-cyan-500 focus:outline-none"
                            placeholder={`Grupo ${newGroupData.numero || 'X'}`}
                            value={newGroupData.nombre}
                            onChange={e => setNewGroupData({ ...newGroupData, nombre: e.target.value })}
                        />
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                title="Eliminar Grupo"
                footer={
                    <>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            className="text-gray-400 hover:text-white px-4 py-2"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            className="bg-red-500 hover:bg-red-400 text-white px-4 py-2 rounded-lg font-bold"
                        >
                            Sí, Eliminar
                        </button>
                    </>
                }
            >
                <p className="text-gray-300">
                    ¿Estás seguro de que quieres eliminar el grupo <strong className="text-white">{groupToDelete?.nombre}</strong>?
                    <br />
                    <span className="text-sm text-red-300 mt-2 block">Esta acción no se puede deshacer.</span>
                </p>
            </Modal>
        </div>
    );
}
